import React, { Fragment, ReactElement, useMemo } from 'react'
import clsx from 'clsx'
import { dark75 } from 'lib/Colors'
import {
	Collapse,
	List,
	ListItem,
	ListItemIcon,
	Tooltip,
	useMediaQuery,
	useTheme,
} from '@material-ui/core'
import { Icons, Row, Typography } from '@copyrightagent/basic-components'
import Image from 'material-ui-image'
import { useTranslation } from 'react-i18next'
import { SystemImages } from 'assets'
import { useRouter } from 'routes/CustomBrowserRouter'
import { getListUrlBasedOnListType, RouteUrls } from 'routes/RouteUrls'
import { EListType } from 'lib/entities/Case'
import { useCaseStatistics } from 'hooks/useCaseStatistics'
import { useStyle, useStyleTooltip } from './style'
import { usePartnerSettingsStore } from '../../../../zustand-store/store'

export const drawerWidth = 240

function ListItemLink(props: any) {
	const router = useRouter()

	const redirectTo = (route: string) => {
		// crutch for inadequate manual review behavior
		if (
			router.location.pathname.includes(route) &&
			route === RouteUrls.inReview
		)
			return
		router.history.push(route)
	}

	return (
		<ListItem button {...props} onClick={redirectTo.bind(null, props.href)}>
			{props.children}
		</ListItem>
	)
}

interface INavRoute {
	icon: (props: any) => ReactElement
	labelString: string
	route: string
	regex: RegExp
}

export default function Navigation(): ReactElement {
	const { registrationCertificatesEnabled, transferAgreementsEnabled } =
		usePartnerSettingsStore()

	const navRoutes: INavRoute[] = [
		{
			icon: Icons.DashboardIcon,
			labelString: 'navigation/dashboard',
			route: RouteUrls.dashboard,
			regex: new RegExp(`${RouteUrls.dashboard}$`),
		},
		{
			icon: Icons.InReviewIcon,
			labelString: 'navigation/inReview',
			route: getListUrlBasedOnListType({
				listType: EListType.inReview,
				resetFilter: true,
			}),
			regex: new RegExp(`${RouteUrls.inReviewList}(.*)`),
		},
		{
			icon: Icons.SearchIcon,
			labelString: 'navigation/findCase',
			route: getListUrlBasedOnListType({
				listType: EListType.all,
				resetFilter: true,
			}),
			regex: new RegExp(`${RouteUrls.findCaseList}(.*)`),
		},
		{
			icon: Icons.ImageImportIcon,
			labelString: 'navigation/images',
			route: RouteUrls.images,
			regex: new RegExp(RouteUrls.images),
		},
		{
			icon: Icons.FileIcon,
			labelString: 'navigation/createCase',
			route: RouteUrls.submitCase,
			regex: new RegExp(RouteUrls.submitCase),
		},
		...(registrationCertificatesEnabled
			? [
					{
						icon: Icons.FileIcon,
						labelString: 'navigation/certificates',
						route: RouteUrls.registrationCertificates,
						regex: new RegExp(RouteUrls.registrationCertificates),
					},
			  ]
			: []),
		...(transferAgreementsEnabled
			? [
					{
						icon: Icons.FileIcon,
						labelString: 'navigation/transferAgreements',
						route: RouteUrls.transferAgreements,
						regex: new RegExp(RouteUrls.transferAgreements),
					},
			  ]
			: []),
	]

	const navBottomRoutes: INavRoute[] = [
		{
			icon: Icons.ReportsIcon,
			labelString: 'navigation/reports',
			route: RouteUrls.reports,
			regex: new RegExp(`${RouteUrls.reports}(.*)`),
		},
		{
			icon: Icons.SettingsIcon,
			labelString: 'navigation/settings',
			route: RouteUrls.settings,
			regex: new RegExp(`${RouteUrls.settings}(.*)`),
		},
	]

	const theme = useTheme()
	const classes = useStyle()
	const toolTipClasses = useStyleTooltip()
	const wide = useMediaQuery(theme.breakpoints.up('lg'))
	const {
		onHold: { count: onHoldCount },
	} = useCaseStatistics()
	const router = useRouter()
	const { t } = useTranslation()

	const logoProps = {
		disableSpinner: true,
		disableTransition: true,
		style: {
			padding: 0,
			margin: '35px auto',
			height: 30,
			width: wide ? 130 : 30,
		},
		imageStyle: { objectFit: 'contain' },
		src: SystemImages.logoWithText,
	}

	const renderOnHoldOption = useMemo(() => {
		const selected = router.location.pathname.match('on-hold') !== null

		return (
			<Collapse in={!!onHoldCount}>
				<ListItemLink
					classes={{
						root: clsx(classes.listItem, classes.onHoldOption),
						selected: classes.selectedListItem,
					}}
					disableTouchRipple
					selected={selected}
					href={getListUrlBasedOnListType({
						listType: EListType.onHold,
						resetFilter: true,
					})}
				>
					<Tooltip
						title={t('putOnHold/casesOnHold') as string}
						classes={toolTipClasses}
						placement="right"
						arrow
					>
						<ListItemIcon classes={{ root: classes.listItemIcon }}>
							<Icons.PauseIcon color={dark75} />
						</ListItemIcon>
					</Tooltip>
					<Row justify="space-between" className={classes.onHoldOptionTextWrap}>
						<Typography className={classes.linkNonActive}>
							{t('putOnHold/casesOnHold')}
						</Typography>
						<Typography fontWeight="bold">{onHoldCount}</Typography>
					</Row>
				</ListItemLink>
			</Collapse>
		)
	}, [classes, onHoldCount, router.location.pathname, t, toolTipClasses])

	return (
		<div className={classes.root}>
			<Image {...logoProps} />
			<List className={classes.list}>
				{navRoutes.map((item, index) => {
					const label = t(item.labelString)
					const selected = router.location.pathname.match(item.regex) !== null

					return (
						<Fragment key={index}>
							<ListItemLink
								classes={{
									root: classes.listItem,
									selected: classes.selectedListItem,
								}}
								disableTouchRipple
								selected={selected}
								href={item.route}
							>
								<Tooltip
									title={label}
									classes={toolTipClasses}
									placement="right"
									arrow
								>
									<ListItemIcon classes={{ root: classes.listItemIcon }}>
										<item.icon color={dark75} />
									</ListItemIcon>
								</Tooltip>
								<Typography
									className={clsx({
										[classes.linkActive]: selected,
										[classes.linkNonActive]: !selected,
									})}
								>
									{label}
								</Typography>
							</ListItemLink>
							{item.route.indexOf(RouteUrls.inReviewList) >= 0 &&
								renderOnHoldOption}
						</Fragment>
					)
				})}
			</List>
			<List className={classes.bottomList}>
				{navBottomRoutes.map((item, index) => {
					const label = t(item.labelString)
					const selected = router.location.pathname.match(item.regex) !== null
					return (
						<ListItemLink
							key={index}
							classes={{
								root: classes.listItem,
								selected: classes.selectedListItem,
							}}
							disableTouchRipple
							selected={selected}
							href={item.route}
						>
							<Tooltip
								title={label}
								classes={toolTipClasses}
								placement="right"
								arrow
							>
								<ListItemIcon classes={{ root: classes.listItemIconProfile }}>
									<item.icon color={dark75} />
								</ListItemIcon>
							</Tooltip>
							<Typography
								className={clsx({
									[classes.linkActive]: selected,
									[classes.linkNonActive]: !selected,
								})}
							>
								{label}
							</Typography>
						</ListItemLink>
					)
				})}
			</List>
		</div>
	)
}
